// shared-lib/cabinet-runs/refAnchors.js
//
// A typed endpoint ref (reference-docs/layout-relationships §2.2) resolved to
// a WALL PARAM on one run segment: an opening's centre or edge, a wall's
// start / midpoint / end, an appliance or bay's stored anchor. Null when the
// ref does not live on this segment's wall — the link is then `off-wall`
// for this run, never silently satisfied elsewhere.
//
// Opening refs name the STRUCTURE id (`canonicalOpeningId`); `ctx.openings`
// may hold hydrated room copies (`id` = room copy, `structureOpeningId` =
// structure id) or raw structure openings, so both are matched.

const {
  worldToWallParam,
  getWallAxes,
} = require('../kitchen-assembly/wallMath');
const { normalizeRef } = require('../group-customization/refs');
const { baysForRun, isApplianceKind } = require('./bays');
const { runAliases } = require('./runIds');

/**
 * Does a `{kind:'bay'}` ref name this bay? `bayId` is the STABLE key
 * (#1526); the anchorParam form is legacy data written before refs carried
 * ids — it strands the moment the param mutates, which the center-on
 * apply's own write-back does.
 */
function bayMatchesRef(bay, r) {
  if (r?.bayId != null) return bay?.id === r.bayId;
  return Math.abs((bay?.anchorParam ?? NaN) - (r?.anchorParam ?? NaN)) < 1e-4;
}

/** The ref's runId names this run under ANY id it has carried (corner-join
 * aliases included — refs written pre-join must keep matching). */
function refRunMatches(run, r) {
  return r?.runId === run?.id || runAliases(run).includes(r?.runId);
}

function openingById(openings, id) {
  return (openings || []).find(
    (o) => o && (o.structureOpeningId === id || o.id === id)
  );
}

/**
 * @param {Object|string} ref
 * @param {{ run, segIndex, openings?, walls? }} ctx — `run` rehydrated
 *   (segments carry `.wall`)
 * @returns {number|null}
 */
function anchorParamForRef(ref, ctx) {
  const r = normalizeRef(ref);
  if (!r) return null;
  const seg = ctx?.run?.segments?.[ctx.segIndex];
  const wall = seg?.wall;
  if (!wall) return null;

  if (r.kind === 'opening') {
    const o = openingById(ctx.openings, r.id);
    if (!o?.center || !o.dimensions) return null;
    const ownerId = (o.parentWallIds || [])[0];
    const half = (o.dimensions.x || 0) / 2;
    if (ownerId === wall.id) {
      const p = worldToWallParam(wall, o.center);
      if (p == null) return null;
      if (r.edge === 'left') return p - half;
      if (r.edge === 'right') return p + half;
      return p;
    }
    // An opening on ANOTHER wall (the generator squares a run end to the
    // edge of a doorway across the room): take the edge point along the
    // owner wall — `left` is the lower owner-wall param, the frame the
    // emitter wrote it in — and project it onto this run's wall.
    // Only an EDGE, and only across the room (owner anti-parallel to this
    // wall): a window on the perpendicular wall has no meaningful param
    // here and stays `off-wall`.
    if (r.edge !== 'left' && r.edge !== 'right') return null;
    const owner = (ctx.walls || []).find((w) => w?.id === ownerId);
    const ax = owner && getWallAxes(owner);
    const mine = getWallAxes(wall);
    if (!ax || !mine) return null;
    const dot = ax.wallDir.x * mine.wallDir.x + ax.wallDir.z * mine.wallDir.z;
    if (dot > -0.9) return null;
    const s = r.edge === 'left' ? -1 : 1;
    return worldToWallParam(wall, {
      x: o.center.x + s * half * ax.wallDir.x,
      z: o.center.z + s * half * ax.wallDir.z,
    });
  }
  if (r.kind === 'wall') {
    if (r.id !== wall.id) return null;
    const axes = getWallAxes(wall);
    if (!axes) return null;
    if (r.at === 'start') return 0;
    if (r.at === 'end') return axes.wallLen;
    return axes.wallLen / 2;
  }
  if (r.kind === 'appliance' || r.kind === 'bay') {
    const bay = baysForRun(ctx.run).find((b) =>
      r.kind === 'appliance'
        ? b.fixtureGroupId === r.fixtureGroupId
        : refRunMatches(ctx.run, r) && bayMatchesRef(b, r)
    );
    if (!bay || (bay.segmentIndex ?? 0) !== ctx.segIndex) return null;
    return bay.anchorParam;
  }
  return null;
}

/** The appliance bays of `run` a ref names (appliance / bay refs only). */
function baysForRef(ref, run) {
  const r = normalizeRef(ref);
  if (!r) return [];
  const bays = baysForRun(run).filter((b) => isApplianceKind(b.kind));
  if (r.kind === 'appliance') {
    return bays.filter((b) => b.fixtureGroupId === r.fixtureGroupId);
  }
  if (r.kind === 'bay') {
    if (!refRunMatches(run, r)) return [];
    return bays.filter((b) => bayMatchesRef(b, r));
  }
  return [];
}

module.exports = {
  anchorParamForRef,
  baysForRef,
  openingById,
  bayMatchesRef,
  refRunMatches,
};
